import { ProgramVisitBadge as DesignSystemProgramVisitBadge } from '@quickmd-llc/design-system'
import { useSelector } from 'store/hooks/useSelector'
import { selectProgramVisitBadgeFlag } from 'store/flags/FlagsSelectors'

type Props = {
  className?: string
  /**
   * The visit's `Coverage` block. `unknown` because every field on the generated
   * contract is optional and the waiting-room list arrives through untyped
   * Redux — the design-system badge parses it, and that parse is the render gate.
   */
  coverage: unknown
}

/**
 * `Quit 7-OH Plan` — the chip a support-plan visit wears wherever a clinician
 * sees that visit: the live control bar, the in-visit patient card, the Home
 * schedule row. Since QMD-4439 the label is the plan name only; the "Visit X of
 * Y" count is no longer shown.
 *
 * The chip itself is `@quickmd-llc/design-system`'s, because provider, admin and
 * the patient apps all draw the same one. What this wrapper adds is provider's
 * two local concerns, neither of which can live in a shared package.
 *
 * The first is the kill switch, which reads this app's Redux. Reading it here
 * rather than at each placement means turning `program_visit_badge_provider` off
 * removes every chip in the app instead of the subset whoever added the newest
 * surface remembered to gate.
 *
 * The second is the `tailwind-scoped` wrapper, which is not decoration.
 * `src/index.css` nests all three `@tailwind` directives inside
 * `.tailwind-scoped`, so every utility is a descendant selector and a DS
 * component on a legacy page gets *nothing* — which is exactly what shipped: the
 * label rendered as unstyled dark navy text wrapping onto two lines on the
 * patient chart and the past-visit notes, both of which are legacy SCSS with no
 * scoped ancestor. Two of the four call sites (the live control bar, the Home
 * schedule row) sit under `WaitingRoom`, which is scoped — which is why the chip
 * looked correct there. Scoping here rather than at each call site means it
 * cannot be got wrong the next time someone drops the chip somewhere new. See
 * `.tailwind-scoped-contents` for why the wrapper has no box of its own; it is
 * also why the wrapper is rendered unconditionally, even on the visits the badge
 * declines to draw.
 */
export function ProgramVisitBadge({ className, coverage }: Props) {
  const isProgramVisitBadgeEnabled = useSelector(selectProgramVisitBadgeFlag)

  if (!isProgramVisitBadgeEnabled) {
    return null
  }

  return (
    <span className='tailwind-scoped tailwind-scoped-contents'>
      <DesignSystemProgramVisitBadge className={className} coverage={coverage} />
    </span>
  )
}
